import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { toast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  FileText, Plus, Check, X, ChevronRight, AlertCircle,
  Building2, Clock, CheckCircle, Eye, MessageSquare,
  Loader2, RefreshCw, Search, Download
} from 'lucide-react';
// Phase B (RCA-6 follow-up, 2026-05-21): TreatX-side admin endpoints
// (`/api/admin/applications*`, `/api/admin/providers`, `/api/admin/document-types*`,
// `/api/admin/requirement-rules*`, `/api/admin/documents/*/review`) are gated by
// the canonical `entity.view` / `entity.manage` permissions. This page is the
// active consumer mounted at `/#/treatx-admin/provider-domain`; every admin
// fetch on this page MUST attach the canonical `treatx_unified_token` Bearer
// header via `authHeaders()` from `utils/unifiedAuth`. Previously the file used
// raw `fetch(...)` calls, which the backend hard-fails with 401 and the FE
// silently rendered "No applications found" — the bug we are closing.
import { authHeaders, maybeClearStaleToken } from '../../utils/unifiedAuth';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_COLORS = {
  submitted: 'bg-blue-100 text-blue-700',
  under_review: 'bg-amber-100 text-amber-700',
  info_requested: 'bg-orange-100 text-orange-700',
  resubmitted: 'bg-purple-100 text-purple-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

const DOC_STATUS_COLORS = {
  uploaded: 'bg-blue-100 text-blue-700',
  under_review: 'bg-amber-100 text-amber-700',
  pending_review: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  verified: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

// ─── Main Component ───────────────────────────────────────────────────
export default function AdminProviderDomain() {
  const [tab, setTab] = useState('applications');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-provider-domain">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <h1 className="text-lg font-bold text-slate-900">Provider Management</h1>
          <div className="flex gap-1 mt-2">
            {['applications', 'providers', 'document-types', 'requirement-rules'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1.5 text-sm rounded-md transition ${
                  tab === t ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`tab-${t}`}
              >
                {t.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {tab === 'applications' && <ApplicationsTab />}
        {tab === 'providers' && <ProvidersTab />}
        {tab === 'document-types' && <DocumentTypesTab />}
        {tab === 'requirement-rules' && <RequirementRulesTab />}
      </div>
    </div>
  );
}

// ─── Applications Tab ─────────────────────────────────────────────────
const APPROVAL_ERROR_LABELS = {
  MANDATORY_DOCUMENTS_NOT_VERIFIED: 'Some mandatory documents are not verified yet.',
  LEGAL_JOURNEY_ACTIVATION_BLOCKED: 'Provider activation legal requirements are not satisfied.',
  existing_user_role_conflict: 'This email already has a conflicting user role.',
  approval_blocked: 'Approval is blocked by a validation gate.',
  requirement_engine_error: 'The requirement engine could not validate this application.',
};

// Turn a fail-closed backend approval/review response into a safe, readable
// Admin message. Handles both string details and structured {code,message}
// payloads; never exposes stack traces/secrets.
function approvalErrorMessage(data) {
  const d = data?.detail;
  if (d && typeof d === 'object') {
    const code = d.code || d.error || '';
    const base = APPROVAL_ERROR_LABELS[code] || d.message || code || 'Action failed.';
    return d.message && d.message !== base ? `${base} (${d.message})` : base;
  }
  if (typeof d === 'string') return APPROVAL_ERROR_LABELS[d] || d;
  return data?.message || 'Action could not be completed. Please try again.';
}

function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [authError, setAuthError] = useState('');

  const fetchApps = useCallback(async () => {
    setLoading(true);
    setAuthError('');
    try {
      const [appsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/admin/applications`, { headers: authHeaders(), cache: 'no-store' }),
        fetch(`${API}/api/admin/applications/stats`, { headers: authHeaders(), cache: 'no-store' }),
      ]);
      // RBAC-FE token-staleness handler: a 401 means the JWT was rejected.
      // TREATX-P0-PROVIDER-APPLICATIONS-503-01: also catches the soft-launch
      // gate 503 (anonymous request blocked by the ASGI gate). In both
      // cases the helper clears the session and replaces the URL with
      // `/login`, so we just abort here.
      if (maybeClearStaleToken(appsRes) || maybeClearStaleToken(statsRes)) return;
      if (!appsRes.ok || !statsRes.ok) {
        const code = !appsRes.ok ? appsRes.status : statsRes.status;
        setAuthError(
          code === 401
            ? 'Authentication required. Please sign in as a TreatX admin to view applications.'
            : code === 403
              ? 'Forbidden. Your role lacks the `entity.view` permission required to view applications.'
              : code === 503
                ? 'Your session has expired or the soft-launch gate blocked an anonymous request. Please sign in as a TreatX admin and try again.'
                : `Failed to load applications (HTTP ${code}).`
        );
        setApps([]);
        setStats({});
        setLoading(false);
        return;
      }
      const appsData = await appsRes.json();
      const statsData = await statsRes.json();
      setApps(appsData.applications || []);
      setStats(statsData.stats || {});
    } catch (e) {
      console.error(e);
      setAuthError(`Network error: ${e.message || 'failed to reach backend'}.`);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const [pendingApprove, setPendingApprove] = useState(null);

  const handleAction = async (appId, action, body = {}) => {
    // Phase 2 — intercept Approve to show a confirmation modal when there
    // are unverified documents. The modal state is lifted here so the
    // modal itself can live at the root of the Applications tab.
    if (action === 'approve') {
      const summary = body.verificationSummary || selected?.verification_summary;
      const unverified = summary?.unverified || [];
      if (unverified.length > 0) {
        setPendingApprove({ appId, summary });
        return;
      }
    }
    try {
      const res = await fetch(`${API}/api/admin/applications/${appId}/${action}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (maybeClearStaleToken(res)) return;
      const data = await res.json().catch(() => ({}));
      // Fail-closed backend gates (mandatory docs, legal activation, RBAC
      // conflict, Requirement Engine) return non-2xx. Surface the real error
      // so "Approve Application" can never look like a dead button.
      if (!res.ok || !data.success) {
        toast({ title: action === 'approve' ? 'Approval blocked' : 'Action failed', description: approvalErrorMessage(data), variant: 'destructive' });
        return;
      }
      await fetchApps();
      if (selected?.application_id === appId) await fetchDetail(appId);
    } catch (e) {
      toast({ title: action === 'approve' ? 'Approval blocked' : 'Action failed', description: String(e?.message || e), variant: 'destructive' });
    }
  };

  const confirmApprove = async () => {
    if (!pendingApprove) return;
    const appId = pendingApprove.appId;
    setPendingApprove(null);
    try {
      const res = await fetch(`${API}/api/admin/applications/${appId}/approve`, {
        method: 'PUT',
        headers: authHeaders(),
      });
      if (maybeClearStaleToken(res)) return;
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        toast({ title: 'Approval blocked', description: approvalErrorMessage(data), variant: 'destructive' });
        return;
      }
      await fetchApps();
      if (selected?.application_id === appId) await fetchDetail(appId);
    } catch (e) {
      toast({ title: 'Approval blocked', description: String(e?.message || e), variant: 'destructive' });
    }
  };

  const fetchDetail = async (appId) => {
    try {
      // Mutable Admin application detail — never serve a cached body, so the
      // post-review refresh always reflects the authoritative backend state.
      const res = await fetch(`${API}/api/admin/applications/${appId}`, { headers: authHeaders(), cache: 'no-store' });
      if (maybeClearStaleToken(res)) return;
      const data = await res.json();
      if (data.success) setSelected(data.application);
    } catch (e) { console.error(e); }
  };

  const handleDocReview = async (docId, status, comment = '', appId = null) => {
    // Deterministic refresh: know exactly which application is being reviewed
    // (explicit appId threaded from ApplicationDetail -> DocumentReviewRow),
    // never rely solely on a possibly-stale `selected` closure.
    const targetAppId = appId || selected?.application_id;
    try {
      const res = await fetch(`${API}/api/admin/documents/${docId}/review`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status, comment }),
      });
      if (maybeClearStaleToken(res)) return false;
      if (!res.ok) {
        let msg = `Review failed (HTTP ${res.status}).`;
        try { const d = await res.json(); msg = (d?.detail?.message || d?.detail || d?.message || msg); } catch (_e) { /* noop */ }
        toast({ title: 'Document review failed', description: String(msg), variant: 'destructive' });
        return false;
      }
      // Await the AUTHORITATIVE refresh BEFORE the action is considered
      // complete, so the mounted ApplicationDetail (badge, status, counters,
      // rejection comment) updates without Back/reopen/manual Refresh/reload.
      if (targetAppId) await fetchDetail(targetAppId);
      await fetchApps();
      toast({ title: status === 'rejected' ? 'Document rejected' : 'Document verified' });
      return true;
    } catch (e) {
      toast({ title: 'Document review failed', description: String(e?.message || e), variant: 'destructive' });
      return false;
    }
  };

  const filtered = apps.filter(a =>
    !filter || a.status === filter
  );

  if (selected) {
    return (
      <>
        <ApplicationDetail app={selected} onBack={() => setSelected(null)} onAction={handleAction} onDocReview={handleDocReview} onRefresh={fetchDetail} />
        <ApproveConfirmModal pending={pendingApprove} onCancel={() => setPendingApprove(null)} onConfirm={confirmApprove} />
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="applications-tab">
      <div className="flex gap-2 flex-wrap">
        {[
          { key: '', label: `All (${stats.total || 0})` },
          { key: 'submitted', label: `Submitted (${stats.submitted || 0})` },
          { key: 'under_review', label: `Review (${stats.under_review || 0})` },
          { key: 'info_requested', label: `Info Req (${stats.info_requested || 0})` },
          { key: 'approved', label: `Approved (${stats.approved || 0})` },
          { key: 'rejected', label: `Rejected (${stats.rejected || 0})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1 text-xs rounded-full border transition ${filter === f.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            data-testid={`filter-${f.key || 'all'}`}
          >
            {f.label}
          </button>
        ))}
        <Button variant="ghost" size="sm" onClick={fetchApps} data-testid="refresh-apps">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {authError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 flex items-start gap-2" data-testid="apps-auth-error">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{authError}</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400" data-testid="apps-empty">{authError ? 'Unable to load applications.' : 'No applications found'}</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(app => (
            <Card key={app.application_id} className="cursor-pointer hover:border-slate-300 transition" onClick={() => fetchDetail(app.application_id)} data-testid={`app-row-${app.application_id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="font-medium text-sm">{app.brand_name || app.legal_entity_name}</p>
                    <p className="text-xs text-slate-400">{app.email} | {app.country}/{app.provider_type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[app.status] || 'bg-slate-100'} data-testid={`status-${app.application_id}`}>{app.status}</Badge>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Phase 2 — Approve-with-unverified confirmation modal ──────────────
function ApproveConfirmModal({ pending, onCancel, onConfirm }) {
  if (!pending) return null;
  const unverified = pending.summary?.unverified || [];
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="approve-confirm-modal"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-slate-900" data-testid="approve-confirm-title">
          Approve with unverified documents?
        </h3>
        <p className="text-sm text-slate-600" data-testid="approve-confirm-body">
          Some documents are not verified. These will remain &quot;Not verified by TreatX&quot;.
        </p>
        <div className="border rounded bg-amber-50 border-amber-200 p-3 text-xs space-y-1" data-testid="approve-unverified-list">
          {unverified.map((u, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="font-medium text-amber-900 flex-shrink-0">{u.document_type}</span>
              <span className="text-amber-800">
                — {u.status === 'rejected' ? 'rejected' : (u.status === 'uploaded' ? 'pending verification' : 'not uploaded')}
                {u.comment ? ` ("${u.comment}")` : ''}
              </span>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="approve-confirm-cancel">
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={onConfirm}
            data-testid="approve-confirm-confirm"
          >
            Confirm Approval
          </Button>
        </div>
      </div>
    </div>
  );
}

// Phase 2 — domain label map, applied as the PRIMARY badge. Raw status is
// still available for CSS color. Copy is intentionally conservative:
//   verified → "Verified by TreatX"
//   rejected → "Not verified by TreatX"   (+ admin comment shown next to it)
//   uploaded → "Pending verification"
const DOMAIN_LABEL = {
  verified: 'Verified by TreatX',
  approved: 'Verified by TreatX',
  rejected: 'Not verified by TreatX',
  uploaded: 'Pending verification',
  pending_review: 'Pending verification',
  pending: 'Pending verification',
};

// ─── Per-Document Review Row (preview/download + per-doc Approve/Reject) ──
function DocumentReviewRow({ doc, appId, onDocReview }) {
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [comment, setComment] = useState(doc.review_comment || '');
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const fileHref = `${API}/api/admin/documents/${doc.document_id}/file`;
  const domainLabel = DOMAIN_LABEL[doc.status] || doc.status;

  // ────────────────────────────────────────────────────────────────────────
  // OWNER-HYGIENE-1 (2026-05-21) — Auth continuity for document download.
  //
  // The admin document file endpoint (`/api/admin/documents/{id}/file`) is
  // gated by `entity.manage` (canonical write authority required). Before
  // this fix the FE rendered a naked `<a href>` opening in `target="_blank"`,
  // which made the browser navigate to that URL WITHOUT the
  // `treatx_unified_token` Authorization header (browsers never forward
  // localStorage tokens on plain navigation). The backend correctly refused
  // with 401 → owner saw raw JSON instead of the file.
  //
  // Canonical fix: authenticated `fetch()` with the Bearer header, materialise
  // the bytes as a `Blob`, hand the browser a temporary `blob:` URL via a
  // synthesised anchor click so the download/preview behaviour stays normal.
  // No new endpoint, no token-in-URL, no public download surface, no
  // weakening of the backend gate.  `maybeClearStaleToken` is preserved so
  // a stale JWT bounces to login as elsewhere.
  // ────────────────────────────────────────────────────────────────────────
  const handleAuthDownload = async () => {
    setDownloading(true);
    setDownloadError('');
    try {
      const res = await fetch(fileHref, { headers: authHeaders() });
      if (maybeClearStaleToken(res)) return;
      if (!res.ok) {
        const code = res.status;
        setDownloadError(
          code === 401
            ? 'Session expired or unauthorized.'
            : code === 403
              ? 'Forbidden (entity.manage required).'
              : code === 404
                ? 'File not found on the server.'
                : `Download failed (HTTP ${code}).`,
        );
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Preserve the original behaviour: open inline in a new tab if the
      // mime type is previewable; otherwise the browser falls back to a
      // download. Use the canonical file_name so the saved file is sensible.
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      if (doc.file_name) a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Release the blob URL on the next tick so the click handler can use it.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setDownloadError(`Network error: ${e.message || 'failed to reach backend'}.`);
    } finally {
      setDownloading(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try { await onDocReview(doc.document_id, 'verified', '', appId); } finally { setSubmitting(false); }
  };

  const handleReject = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await onDocReview(doc.document_id, 'rejected', comment.trim(), appId);
      setShowRejectBox(false);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="ml-6 py-2 border-t" data-testid={`doc-${doc.document_id}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium truncate">{doc.file_name}</p>
          <p className="text-[10px] text-slate-400">
            {doc.expiry_date && `Expires: ${doc.expiry_date}`}
            {doc.review_comment && doc.status === 'rejected' && ` | Comment: ${doc.review_comment}`}
          </p>
          {downloadError && (
            <p className="text-[10px] text-red-600 mt-0.5" data-testid={`doc-download-error-${doc.document_id}`}>
              {downloadError}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Badge className={`text-[10px] ${DOC_STATUS_COLORS[doc.status]}`} data-testid={`doc-status-${doc.document_id}`}>
            {domainLabel}
          </Badge>
          <button
            type="button"
            onClick={handleAuthDownload}
            disabled={downloading}
            className="text-slate-500 hover:text-slate-900 p-1 disabled:opacity-50"
            title="Preview / download"
            data-testid={`view-${doc.document_id}`}
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </button>
          {doc.status !== 'verified' && doc.status !== 'approved' && (
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-emerald-600" onClick={handleApprove} disabled={submitting} data-testid={`verify-${doc.document_id}`}>
              <Check className="h-3.5 w-3.5" />
            </Button>
          )}
          {doc.status !== 'rejected' && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-1.5 text-red-500"
              onClick={() => setShowRejectBox(v => !v)}
              data-testid={`reject-toggle-${doc.document_id}`}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      {showRejectBox && (
        <div className="mt-2 flex items-start gap-2" data-testid={`reject-box-${doc.document_id}`}>
          <Input
            placeholder="Comment required — explain why this document is rejected"
            value={comment}
            onChange={e => setComment(e.target.value)}
            className="h-8 text-xs"
            autoFocus
            data-testid={`reject-comment-${doc.document_id}`}
          />
          <Button
            size="sm"
            variant="destructive"
            className="h-8"
            onClick={handleReject}
            disabled={submitting || !comment.trim()}
            data-testid={`reject-confirm-${doc.document_id}`}
          >
            Reject
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={() => { setShowRejectBox(false); setComment(doc.review_comment || ''); }}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Application Detail ────────────────────────────────────────────────
function ApplicationDetail({ app, onBack, onAction, onDocReview, onRefresh }) {
  const [infoMsg, setInfoMsg] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const summary = app.document_summary || {};
  const lastRefreshedAt = new Date().toLocaleTimeString();

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await onRefresh?.(app.application_id); } finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-4" data-testid="app-detail">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn">&larr; Back</Button>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-detail-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              {/* TREATX-L1-D — reviewer surface: show BRAND as primary
                  heading (patient-facing identity) and the registered
                  LEGAL entity name explicitly beneath, so the
                  reviewer can compare it against uploaded corporate
                  evidence. */}
              <h2 className="text-lg font-bold" data-testid="detail-brand-name">
                {app.brand_name || app.legal_entity_name || '—'}
              </h2>
              {app.legal_entity_name && app.legal_entity_name !== app.brand_name && (
                <p className="text-xs text-slate-600" data-testid="detail-legal-name">
                  <span className="uppercase tracking-wider text-[10px] text-slate-400 mr-2">Legal</span>
                  {app.legal_entity_name}
                </p>
              )}
              <p className="text-sm text-slate-500">{app.application_id} | {app.email}</p>
              <p className="text-xs text-slate-400">{app.country} / {app.city} / {app.provider_type}</p>
            </div>
            <Badge className={`text-sm ${STATUS_COLORS[app.status]}`} data-testid="detail-status">{app.status}</Badge>
          </div>
          <div className="flex gap-2 mt-3 text-xs">
            <span className="px-2 py-1 rounded bg-slate-100">Docs: {summary.total_uploaded || 0}</span>
            <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700">Verified: {summary.verified || 0}</span>
            <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">Mandatory: {summary.mandatory_fulfilled || 0}/{summary.mandatory_total || 0}</span>
          </div>
        </CardContent>
      </Card>

      {/* Requirements + Documents */}
      <Card>
        <CardHeader className="pb-2 space-y-1">
          <CardTitle className="text-sm" data-testid="doc-actions-title">Document-level actions</CardTitle>
          <p className="text-[11px] text-slate-500" data-testid="doc-actions-desc">
            Use these to verify or reject specific documents. Rejecting a document will request new information from the provider.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {(app.requirements || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No requirement rules configured for {app.country}/{app.provider_type}</p>
          ) : (
            (app.requirements || []).map(req => {
              const hasHistory = (req.document_history || []).length > 0;
              const latestUpload = req.latest_upload_at
                ? new Date(req.latest_upload_at)
                : null;
              return (
              <div key={req.rule_id} className="border rounded-lg p-3" data-testid={`req-${req.rule_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span className="font-medium text-sm">{req.document_type_name}</span>
                    {req.mandatory && <Badge className="bg-red-100 text-red-700 text-[10px]">Mandatory</Badge>}
                    {req.expiry_required && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Expiry Req</Badge>}
                    {req.is_resubmission && (
                      <Badge className="bg-blue-100 text-blue-700 text-[10px]" data-testid={`resubmission-badge-${req.rule_id}`}>
                        Resubmitted{latestUpload ? ` ${latestUpload.toLocaleString()}` : ''}
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-slate-400">{req.doc_count}/{req.min_count} active</span>
                </div>
                {(req.documents || []).map((doc, di) => (
                  <div key={doc.document_id} data-testid={`active-doc-wrapper-${doc.document_id}`}>
                    {di === 0 && req.is_resubmission && (
                      <div className="ml-6 text-[10px] uppercase tracking-wide text-blue-700 font-semibold pt-1" data-testid={`current-label-${req.rule_id}`}>
                        Current file
                      </div>
                    )}
                    <DocumentReviewRow doc={doc} appId={app.application_id} onDocReview={onDocReview} />
                  </div>
                ))}
                {req.doc_count === 0 && <p className="ml-6 text-xs text-slate-400 italic">No documents uploaded</p>}

                {hasHistory && (
                  <details className="ml-6 mt-2" data-testid={`history-${req.rule_id}`}>
                    <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-800 select-none">
                      Earlier attempts ({req.document_history.length}) — superseded
                    </summary>
                    <div className="mt-2 space-y-1 border-l-2 border-slate-200 pl-3" data-testid={`history-list-${req.rule_id}`}>
                      {req.document_history.map(hd => (
                        <div key={hd.document_id} className="flex items-center justify-between py-1">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-600 line-through truncate">{hd.file_name}</p>
                            <p className="text-[10px] text-slate-400">
                              Uploaded {hd.uploaded_at ? formatDateTimeLocale(hd.uploaded_at) : '—'}
                              {hd.review_comment && hd.status === 'rejected' && ` | Rejected: "${hd.review_comment}"`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Badge className={`text-[10px] ${DOC_STATUS_COLORS[hd.status] || 'bg-slate-100 text-slate-500'}`}>{hd.status}</Badge>
                            <Badge className="bg-slate-200 text-slate-600 text-[10px]">superseded</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Application-level actions (Phase 4) — clearly separated from
          document-level actions (which live on the Requirements card above).
          Only two actions live here:
            1. Approve Application
            2. Request Additional Information (No document rejection) */}
      <Card data-testid="application-actions-card">
        <CardHeader className="pb-2 space-y-1">
          <CardTitle className="text-sm" data-testid="app-actions-title">Application-level actions</CardTitle>
          <p className="text-[11px] text-slate-500" data-testid="app-actions-desc">
            These actions affect the application as a whole. To verify or reject a specific document, use the Document-level actions above.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {app.status === 'submitted' && (
            <Button size="sm" onClick={() => onAction(app.application_id, 'review')} data-testid="action-review">
              <Eye className="h-3.5 w-3.5 mr-1" /> Start Review
            </Button>
          )}
          {app.status === 'resubmitted' && (
            <Button size="sm" onClick={() => onAction(app.application_id, 'review')} data-testid="action-review-resubmit">
              <Eye className="h-3.5 w-3.5 mr-1" /> Review Resubmission
            </Button>
          )}
          {['under_review', 'info_requested', 'resubmitted'].includes(app.status) && (
            <div className="space-y-4">
              {/* Approve Application — unverified docs trigger a confirmation
                  modal in the parent. Approving does NOT auto-verify docs. */}
              <div className="space-y-1">
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => onAction(app.application_id, 'approve', { verificationSummary: app.verification_summary })}
                  data-testid="action-approve"
                >
                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve Application
                </Button>
              </div>

              {/* Request Additional Information — explicitly NOT a document
                  rejection. Backend PUT /request-info only updates the
                  application status to info_requested and appends a note;
                  it does not modify any document statuses. */}
              <div className="space-y-1 border-t pt-3" data-testid="request-info-section">
                <Label className="text-xs font-medium text-slate-700" data-testid="request-info-label">
                  Request Additional Information (No document rejection)
                </Label>
                <p className="text-[11px] text-slate-500" data-testid="request-info-helper">
                  Use this to ask for clarification or additional details without rejecting any document. This will notify the provider but will not change document statuses.
                </p>
                <div className="flex gap-2 pt-1">
                  <Input
                    placeholder="Message to the applicant (e.g. please clarify opening hours)"
                    value={infoMsg}
                    onChange={e => setInfoMsg(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="info-msg-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { onAction(app.application_id, 'request-info', { message: infoMsg }); setInfoMsg(''); }}
                    disabled={!infoMsg}
                    title="Request Additional Information (No document rejection)"
                    data-testid="action-request-info"
                  >
                    <MessageSquare className="h-3.5 w-3.5 mr-1" /> Request Additional Information
                  </Button>
                </div>
              </div>
            </div>
          )}
          {['approved', 'rejected'].includes(app.status) && (
            <p className="text-xs text-slate-400">Application is {app.status}. No further actions available.</p>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      {(app.notes || []).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Notes</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {app.notes.map((n, i) => (
              <div key={i} className="text-xs border-l-2 border-slate-200 pl-3 py-1">
                <span className="font-medium">{n.type}</span> <span className="text-slate-400">by {n.by} at {formatDateTimeLocale(n.timestamp)}</span>
                <p className="text-slate-600 mt-0.5">{n.message}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Provider public visibility (TREATX-SEO-FINAL-S4.2) ───────────────
// Super-Admin-only, owner-approved. Deliberately separate from Activate /
// Suspend / Deactivate: blocking hides the provider from public search, its
// public profile and the sitemap WITHOUT changing its lifecycle status.
function PublicVisibilityControl({ providerId }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/providers/${providerId}/public-visibility`, { headers: authHeaders() });
      if (maybeClearStaleToken(res)) return;
      const data = await res.json();
      setState(res.ok ? data.visibility : null);
    } catch (e) { console.error(e); setState(null); }
    setLoading(false);
  }, [providerId]);

  useEffect(() => { load(); }, [load]);

  const apply = async (blocked) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/providers/${providerId}/public-visibility`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blocked,
          reason: blocked ? (reason.trim() || null) : null,
          expected_blocked: state?.admin_block?.blocked === true,
        }),
      });
      if (maybeClearStaleToken(res)) return;
      if (res.status === 403) toast({ title: t('providerPublicVisibility.errorForbidden'), variant: 'destructive' });
      else if (res.status === 404) toast({ title: t('providerPublicVisibility.errorNotFound'), variant: 'destructive' });
      else if (res.status === 409) { toast({ title: t('providerPublicVisibility.errorConflict'), variant: 'destructive' }); await load(); }
      else if (!res.ok) toast({ title: t('providerPublicVisibility.errorGeneric'), variant: 'destructive' });
      else {
        // Success is shown only after the server confirmed persistence.
        const data = await res.json();
        setState(data.visibility);
        setReason('');
        toast({ title: t(blocked ? 'providerPublicVisibility.successBlocked' : 'providerPublicVisibility.successUnblocked') });
        await load();
      }
    } catch (e) {
      toast({ title: t('providerPublicVisibility.errorGeneric'), description: String(e?.message || e), variant: 'destructive' });
    }
    setSaving(false);
    setConfirming(false);
  };

  if (loading) return <p className="text-xs text-slate-400" data-testid={`prov-visibility-loading-${providerId}`}>{t('providerPublicVisibility.loading')}</p>;
  if (!state) return null;

  const isBlocked = state.admin_block?.blocked === true;
  return (
    <div className="mt-3 border-t pt-3 space-y-2" data-testid={`prov-visibility-${providerId}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-medium text-slate-700">{t('providerPublicVisibility.title')}</p>
          <p className="text-xs text-slate-400">{t('providerPublicVisibility.description')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            className={isBlocked ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}
            data-testid={`prov-visibility-decision-${providerId}`}
          >
            {t('providerPublicVisibility.storedDecision')}: {t(isBlocked ? 'providerPublicVisibility.blocked' : 'providerPublicVisibility.notBlocked')}
          </Badge>
          <Badge
            className={state.effective_public ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}
            data-testid={`prov-visibility-effective-${providerId}`}
          >
            {t('providerPublicVisibility.effective')}: {t(state.effective_public ? 'providerPublicVisibility.visible' : 'providerPublicVisibility.hidden')}
          </Badge>
        </div>
      </div>

      {(state.blocking_reasons || []).length > 0 && (
        <ul className="text-xs text-slate-500 list-disc pl-4" data-testid={`prov-visibility-reasons-${providerId}`}>
          {state.blocking_reasons.map(r => (
            <li key={r.code}>{t(`providerPublicVisibility.reason_${r.code}`, r.detail)}</li>
          ))}
        </ul>
      )}

      {isBlocked ? (
        <div className="space-y-1">
          {state.admin_block?.by && (
            <p className="text-xs text-slate-400">
              {t('providerPublicVisibility.blockedBy')}: {state.admin_block.by}
              {state.admin_block.at ? ` · ${t('providerPublicVisibility.blockedAt')}: ${formatDateTimeLocale(state.admin_block.at)}` : ''}
              {state.admin_block.reason ? ` · ${state.admin_block.reason}` : ''}
            </p>
          )}
          <Button
            size="sm" variant="outline" disabled={saving}
            onClick={() => apply(false)}
            data-testid={`prov-visibility-unblock-${providerId}`}
          >
            {t('providerPublicVisibility.unblock')}
          </Button>
        </div>
      ) : confirming ? (
        <div className="space-y-2 bg-red-50 border border-red-200 rounded p-2" data-testid={`prov-visibility-confirm-${providerId}`}>
          <p className="text-xs font-medium text-red-700">{t('providerPublicVisibility.confirmBlockTitle')}</p>
          <p className="text-xs text-red-600">{t('providerPublicVisibility.confirmBlockBody')}</p>
          <Label className="text-xs">{t('providerPublicVisibility.reasonLabel')}</Label>
          <Input
            value={reason} onChange={e => setReason(e.target.value)}
            placeholder={t('providerPublicVisibility.reasonPlaceholder')}
            className="h-8 text-xs"
            data-testid={`prov-visibility-reason-${providerId}`}
          />
          <div className="flex gap-2">
            <Button
              size="sm" variant="destructive" disabled={saving}
              onClick={() => apply(true)}
              data-testid={`prov-visibility-confirm-yes-${providerId}`}
            >
              {t('providerPublicVisibility.confirmYes')}
            </Button>
            <Button
              size="sm" variant="outline" disabled={saving}
              onClick={() => { setConfirming(false); setReason(''); }}
              data-testid={`prov-visibility-confirm-cancel-${providerId}`}
            >
              {t('providerPublicVisibility.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="sm" variant="outline" disabled={saving}
          onClick={() => setConfirming(true)}
          data-testid={`prov-visibility-block-${providerId}`}
        >
          {t('providerPublicVisibility.block')}
        </Button>
      )}
    </div>
  );
}

// ─── Providers Tab ─────────────────────────────────────────────────────
function ProvidersTab() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/providers`, { headers: authHeaders() });
        if (maybeClearStaleToken(res)) return;
        const data = await res.json();
        setProviders(data.providers || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="providers-tab">
      <p className="text-sm text-slate-500">Total providers: {providers.length}</p>
      {providers.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400">No providers yet. Providers are created when applications are approved.</CardContent></Card>
      ) : (
        providers.map(p => (
          <Card key={p.provider_id} data-testid={`prov-${p.provider_id}`}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{p.brand_name || p.legal_entity_name}</p>
                  <p className="text-xs text-slate-400">{p.provider_id} | {p.country}/{p.provider_type}</p>
                </div>
                <Badge className={p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : p.status === 'suspended' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{p.status}</Badge>
              </div>
              <PublicVisibilityControl providerId={p.provider_id} />
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

// P6L: hardcoded fallback only if backend omits allowed_categories (defense-in-depth)
const FALLBACK_DOCUMENT_CATEGORIES = [
  'legal', 'identity', 'clinical', 'accreditation',
  'financial', 'insurance', 'compliance', 'other',
];

// ─── Document Types Tab ───────────────────────────────────────────────
function DocumentTypesTab() {
  const [types, setTypes] = useState([]);
  const [allowedCategories, setAllowedCategories] = useState(FALLBACK_DOCUMENT_CATEGORIES);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', display_name: '', category: 'other', expiry_supported: false });
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState('');

  const fetchTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/document-types`, { headers: authHeaders() });
      if (maybeClearStaleToken(res)) return;
      const data = await res.json();
      setTypes(data.document_types || []);
      if (Array.isArray(data.allowed_categories) && data.allowed_categories.length > 0) {
        setAllowedCategories(data.allowed_categories);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const handleCreate = async () => {
    setSaveError('');
    try {
      const res = await fetch(`${API}/api/admin/document-types`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (maybeClearStaleToken(res)) return;
      const data = await res.json();
      if (data.success) {
        setShowForm(false);
        setForm({ code: '', display_name: '', category: 'other', expiry_supported: false });
        fetchTypes();
      } else {
        setSaveError(data.detail || data.message || 'Save failed');
      }
    } catch (e) { setSaveError(e.message || 'Save failed'); }
  };

  const handleToggle = async (dtId, active) => {
    if (active) {
      const res = await fetch(`${API}/api/admin/document-types/${dtId}`, { method: 'DELETE', headers: authHeaders() });
      if (maybeClearStaleToken(res)) return;
    } else {
      const res = await fetch(`${API}/api/admin/document-types/${dtId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ ...types.find(t => t.document_type_id === dtId), active: true }),
      });
      if (maybeClearStaleToken(res)) return;
    }
    fetchTypes();
  };

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="document-types-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{types.length} document types</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-doc-type-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Type
        </Button>
      </div>

      {showForm && (
        <Card className="border-blue-200">
          <CardContent className="py-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Code *</Label><Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="business_license" className="h-8 text-xs" data-testid="dt-code" /></div>
              <div><Label className="text-xs">Display Name *</Label><Input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="Business License" className="h-8 text-xs" data-testid="dt-name" /></div>
              <div>
                <Label className="text-xs">Category</Label>
                <select
                  className="w-full h-8 text-xs border rounded px-2 bg-white"
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  data-testid="dt-category"
                >
                  {allowedCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 pt-4">
                <input type="checkbox" checked={form.expiry_supported} onChange={e => setForm(f => ({ ...f, expiry_supported: e.target.checked }))} data-testid="dt-expiry" />
                <Label className="text-xs">Expiry Supported</Label>
              </div>
            </div>
            {saveError && (
              <p className="text-xs text-red-500" data-testid="dt-save-error">{saveError}</p>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreate} disabled={!form.code || !form.display_name} data-testid="dt-save">Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {types.map(t => (
        <Card key={t.document_type_id} className={!t.active ? 'opacity-50' : ''} data-testid={`dt-row-${t.document_type_id}`}>
          <CardContent className="py-3 flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">{t.display_name}</p>
              <p className="text-xs text-slate-400">{t.code} | {t.category} {t.expiry_supported ? '| Expiry' : ''}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={t.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>{t.active ? 'Active' : 'Inactive'}</Badge>
              <Button size="sm" variant="ghost" onClick={() => handleToggle(t.document_type_id, t.active)} data-testid={`toggle-${t.document_type_id}`}>
                {t.active ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Requirement Rules Tab ─────────────────────────────────────────────
function RequirementRulesTab() {
  const [rules, setRules] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [countries, setCountries] = useState([]); // P6K: {code, name} list from SSOT
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ document_type_id: '', provider_type: 'ALL', country: 'ALL', mandatory: true, expiry_required: false });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [rulesRes, dtRes, cRes] = await Promise.all([
        fetch(`${API}/api/admin/requirement-rules`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/document-types?active_only=true`, { headers: authHeaders() }),
        fetch(`${API}/api/public/catalog/countries-full`),
      ]);
      if (maybeClearStaleToken(rulesRes) || maybeClearStaleToken(dtRes)) return;
      const rulesData = await rulesRes.json();
      const dtData = await dtRes.json();
      const cData = await cRes.json();
      setRules(rulesData.rules || []);
      setDocTypes(dtData.document_types || []);
      setCountries(Array.isArray(cData.countries) ? cData.countries : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API}/api/admin/requirement-rules`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (maybeClearStaleToken(res)) return;
      const data = await res.json();
      if (data.success) {
        setShowForm(false);
        setForm({ document_type_id: '', provider_type: 'ALL', country: 'ALL', mandatory: true, expiry_required: false });
        fetchData();
      }
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (ruleId) => {
    const res = await fetch(`${API}/api/admin/requirement-rules/${ruleId}`, { method: 'DELETE', headers: authHeaders() });
    if (maybeClearStaleToken(res)) return;
    fetchData();
  };

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="requirement-rules-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{rules.length} rules</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-rule-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Rule
        </Button>
      </div>

      {showForm && (
        <Card className="border-blue-200">
          <CardContent className="py-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Document Type *</Label>
                <select className="w-full border rounded p-1.5 text-xs" value={form.document_type_id} onChange={e => setForm(f => ({ ...f, document_type_id: e.target.value }))} data-testid="rule-doc-type">
                  <option value="">Select...</option>
                  {docTypes.map(dt => <option key={dt.document_type_id} value={dt.document_type_id}>{dt.display_name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Provider Type</Label>
                <select className="w-full border rounded p-1.5 text-xs" value={form.provider_type} onChange={e => setForm(f => ({ ...f, provider_type: e.target.value }))} data-testid="rule-prov-type">
                  <option value="ALL">ALL</option>
                  <option value="clinic">Clinic</option>
                  <option value="hospital">Hospital</option>
                  <option value="doctor_practice">Doctor Practice</option>
                  <option value="agency">Agency</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Country</Label>
                <select className="w-full border rounded p-1.5 text-xs" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} data-testid="rule-country">
                  <option value="ALL">ALL — All Countries</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-4 pt-4">
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={form.mandatory} onChange={e => setForm(f => ({ ...f, mandatory: e.target.checked }))} data-testid="rule-mandatory" /> Mandatory</label>
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={form.expiry_required} onChange={e => setForm(f => ({ ...f, expiry_required: e.target.checked }))} data-testid="rule-expiry" /> Expiry Required</label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreate} disabled={!form.document_type_id} data-testid="rule-save">Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {rules.map(r => (
        <Card key={r.rule_id} className={!r.active ? 'opacity-50' : ''} data-testid={`rule-row-${r.rule_id}`}>
          <CardContent className="py-3 flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">{r.document_type_name || r.document_type_id}</p>
              <p className="text-xs text-slate-400">
                {r.country} / {r.provider_type}
                {r.mandatory && ' | Mandatory'}
                {r.expiry_required && ' | Expiry Required'}
              </p>
            </div>
            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(r.rule_id)} data-testid={`delete-rule-${r.rule_id}`}>
              <X className="h-3 w-3" />
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
